/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import React, { useRef } from 'react';
import backClouds from '../../images/hero_cloud_back.png';
import frontClouds from '../../images/hero_cloud_front.webp';
import topCloud from '../../images/hero_cloud_top.webp';
import table from '../../images/hero_table.webp';
import { BackgroundColorTheme } from '../../templates/Page';
import { blendEases } from '../../utils/gsap';
import { usePersistentState } from '../../utils/hooks';
import { clsx } from '../../utils/utils';
import * as styles from './HomepageHero.module.scss';
import ScrollingItems from './ScrollingItems';

const TABLE_IMAGE_WIDTH_PX = 1140;

// Reference text "Dining, Elevated" (16 chars) looks correct at the default h1 sizes.
// Scale font size inversely with character count so any CMS text occupies similar visual width.
const REFERENCE_CHAR_COUNT = 16;
const TITLE_SCALE_FACTOR = 0.8; // 0 = no scaling, 1 = fully inverse

function getTitleFontScale(title: string): number {
  const ratio = REFERENCE_CHAR_COUNT / title.length;
  // Blend between 1 (no change) and the full ratio, clamped to a reasonable range
  return Math.min(Math.max(1 + (ratio - 1) * TITLE_SCALE_FACTOR, 0.5), 1.3);
}

export type HeroType = {
  title: string;
};

type HeroProps = HeroType & {
  theme?: BackgroundColorTheme;
  className?: string;
  disableAnimation?: boolean;
};

const HomepageHero = ({ title, className, disableAnimation }: HeroProps): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasLoadedBefore, setHasLoadedBefore] = usePersistentState<boolean>(
    'hasLoadedBefore',
    false,
    'session',
  );

  useGSAP(
    () => {
      // HACK: Somehow fixes jumping table bug ¯\_(ツ)_/¯
      window.scrollBy({ top: 1 });

      const mm = gsap.matchMedia();

      mm.add(
        {
          isDesktop: '(min-width: 1200px)',
          isTablet: '(min-width: 720px) and (max-width: 1199px)',
          isMobile: '(max-width: 719px)',
          isTallScreen: '(min-height: 768px)',
          isShortScreen: '(max-height: 767px)',
        },
        context => {
          const { isTallScreen, isDesktop } = context.conditions || {};

          const tl = gsap.timeline();

          if (disableAnimation) {
            tl.set(`.${styles.title}`, { autoAlpha: 1 }).set(`.${styles.table}`, {
              autoAlpha: 1,
              x: isDesktop ? '-50%' : '0%',
            });
          } else {
            const titleSplit = new SplitText(`.${styles.title}`, { type: 'words' });
            tl.set(`.${styles.title}`, { autoAlpha: 1 })
              .set(`.${styles.table}`, {
                autoAlpha: 1,
                x: isDesktop ? '-50%' : '0%',
              })
              .from(titleSplit.words, {
                delay: 0.5,
                y: 20,
                autoAlpha: 0,
                duration: 1,
                stagger: 1.2,
              });
          }

          tl.from(
              `.${styles.title}`,
              {
                bottom: isTallScreen ? '0%' : '20%',
                scale: isTallScreen ? 1.25 : 1.1,
                delay: 1,
                duration: 1.5,
                ease: 'power1.inOut',
              },
              '-=0.8',
            )
            // Intro runs on the intro wrapper (y), yoyo on the img (y), parallax
            // on the transform wrapper (yPercent). Three elements so no two
            // tweens fight over the same transform sub-property. See
            // performance-plan.md for the history on why this matters.
            .from(
              `.${styles.tableIntroWrapper}`,
              {
                y: (Math.max(TABLE_IMAGE_WIDTH_PX, window.innerWidth * 0.8) / 4) * 1.5 - 150,
                duration: 2.5,
                ease: blendEases('power1.in', 'elastic.out(5, 0.7)'),
              },
              '<',
            )
            .to(
              `.${styles.table}`,
              {
                y: 20,
                duration: 8,
                repeat: -1,
                yoyo: true,
                ease: 'power1.inOut',
              },
              '-=1.5',
            );

          // parallax
          gsap.to(`.${styles.tableTransformWrapper}`, {
            yPercent: 50,
            scrollTrigger: {
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            } satisfies ScrollTrigger.Vars,
          });
          gsap.to(`.${styles.backClouds}`, {
            yPercent: 200,
            scrollTrigger: {
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            } satisfies ScrollTrigger.Vars,
          });

          // run animation on load only
          if (hasLoadedBefore) {
            tl.seek('100%');
          } else {
            setHasLoadedBefore(true);
          }
        },
      );
    },
    { scope: containerRef },
  );

  return (
    <div ref={containerRef} className={clsx(styles.container, className)} data-theme="panoramic">
      <div className={styles.topContainer}>
        <ScrollingItems
          animationDuration={400}
          animationDirection="right"
          className={styles.topCloud}
        >
          <img src={topCloud} alt="" width={813} height={444} />
        </ScrollingItems>
        <ScrollingItems
          animationDuration={400}
          animationDirection="right"
          className={styles.backClouds}
        >
          <img src={backClouds} alt="" width={4096} height={659} />
        </ScrollingItems>
        <div className={styles.textContainer}>
          <h1
            className={styles.title}
            style={{ fontSize: `calc(var(--hero-base-font-size) * ${getTitleFontScale(title)})` }}
          >
            {title}
          </h1>
        </div>
        <div className={styles.tableContainer}>
          <div className={styles.tableTransformWrapper}>
            <div className={styles.tableIntroWrapper}>
              {/* No width/height attrs: the source asset is 2051×3060 (mostly
                  transparent cloudscape with the table at the bottom). With
                  HTML dims the browser reserves a 1140×1700 box before load,
                  which makes yPercent resolve against 1700px and produces a
                  massive elastic overshoot on entry. */}
              <img
                src={table}
                className={styles.table}
                alt=""
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
      <ScrollingItems
        animationDuration={100}
        animationDirection="right"
        className={styles.frontClouds}
      >
        <img src={frontClouds} alt="" width={2168} height={520} />
      </ScrollingItems>
    </div>
  );
};

export default HomepageHero;
